import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, CheckCircle2, CircleDot } from "lucide-react";
import { fetchPlan } from "../lib/api";
import { getPlanProgress, summarizePlanProgress } from "../lib/readingProgress";
import { Skeleton } from "../components/ui/skeleton";

export default function PlanoDetalhe() {
  const { slug } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    fetchPlan(slug).then(setPlan).catch(() => {}).finally(() => setLoading(false));
    setProgress(getPlanProgress(slug));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto section-padding py-8">
        <Skeleton className="h-40 w-full rounded-2xl mb-6" />
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    );
  }

  if (!plan) {
    return <div className="section-padding py-8"><p className="text-navy/60">Plano não encontrado.</p></div>;
  }

  const summary = summarizePlanProgress(slug, plan.duration_days);
  const completedSet = new Set(progress?.completed_days || []);
  const nextDay = !summary.completed ? summary.current_day : null;

  return (
    <div data-testid="plan-detail-page">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: plan.cover_color || "#0B1E3F" }}
      >
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_15%_20%,hsl(var(--gold))_0%,transparent_45%)]" />
        <div className="relative max-w-3xl mx-auto section-padding py-12 text-white">
          <Link
            to="/planos"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 hover:text-gold mb-8"
            data-testid="plan-back"
          >
            <ArrowLeft className="w-4 h-4" /> Todos os planos
          </Link>
          <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
            Plano de leitura
          </span>
          <h1 className="font-display text-4xl sm:text-5xl mt-4 leading-[1.1]" data-testid="plan-title">
            {plan.title}
          </h1>
          <p className="text-white/75 mt-4 max-w-xl">{plan.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gold">
            <span className="inline-flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {plan.duration_days} dias de jornada
            </span>
            {plan.category && (
              <span
                data-testid="plan-category"
                className="inline-flex items-center rounded-full border border-gold/40 px-3 py-0.5 text-[11px] uppercase tracking-widest"
              >
                {plan.category}
              </span>
            )}
            {plan.audience && (
              <span
                data-testid="plan-audience"
                className="inline-flex items-center rounded-full border border-white/20 px-3 py-0.5 text-[11px] uppercase tracking-widest text-white/75"
              >
                {plan.audience}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Objetivo espiritual (P1) */}
      {plan.objective && (
        <section className="max-w-3xl mx-auto section-padding pt-8">
          <div
            data-testid="plan-objective"
            className="rounded-2xl border border-gold/40 bg-[hsl(var(--parchment))] px-5 py-4 sm:p-5"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">
              Objetivo espiritual
            </p>
            <p className="text-navy leading-relaxed">{plan.objective}</p>
          </div>
        </section>
      )}

      {/* Progress summary */}
      {summary.started && (
        <section className="max-w-3xl mx-auto section-padding -mt-6 relative z-10">
          <div
            className="bg-white border border-border rounded-2xl p-5 shadow-sm"
            data-testid="plan-progress-summary"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {summary.completed ? "Plano concluído" : "Sua jornada"}
              </p>
              <span className="text-xs text-navy/55" data-testid="plan-progress-percent">
                {summary.percent}%
              </span>
            </div>
            <p className="font-display text-xl text-navy" data-testid="plan-progress-count">
              Você concluiu {summary.completed_days_count} de {summary.total_days} dias
            </p>
            {!summary.completed && nextDay && (
              <p className="text-sm text-navy/65 mt-1" data-testid="plan-progress-next">
                Próximo: Dia {nextDay}
              </p>
            )}
            <div className="mt-3 h-1 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-gold transition-all duration-500"
                style={{ width: `${summary.percent}%` }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Days list */}
      <section className="max-w-3xl mx-auto section-padding py-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50 mb-6">
          Dias da jornada
        </p>
        <div className="space-y-3">
          {(plan.days || []).map((d) => {
            const isCompleted = completedSet.has(d.day);
            const isNext = !summary.completed && nextDay === d.day && !isCompleted;
            const StatusIcon = isCompleted ? CheckCircle2 : (isNext ? CircleDot : null);
            const statusLabel = isCompleted ? "Concluído" : (isNext ? "Próximo" : "");
            return (
              <Link
                key={d.day}
                to={`/planos/${plan.slug}/dia/${d.day}`}
                data-testid={`plan-day-item-${d.day}`}
                data-status={isCompleted ? "completed" : (isNext ? "next" : "future")}
                className={`group flex items-center gap-5 bg-white border rounded-2xl p-5 card-lift ${
                  isNext ? "border-gold/60" : "border-border"
                }`}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center font-display text-lg shrink-0"
                  style={{
                    background: isCompleted ? "hsl(var(--gold))" : "hsl(var(--parchment))",
                    color: isCompleted ? "hsl(var(--navy))" : "hsl(var(--navy))",
                  }}
                >
                  {String(d.day).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg text-navy leading-snug">{d.title}</h3>
                  <p className="text-xs text-gold mt-1">{d.verse_ref}</p>
                  {statusLabel && (
                    <p
                      className="text-[10px] uppercase tracking-[0.25em] text-navy/55 mt-1.5 inline-flex items-center gap-1.5"
                      data-testid={`plan-day-status-${d.day}`}
                    >
                      {StatusIcon && <StatusIcon className="w-3 h-3" />} {statusLabel}
                    </p>
                  )}
                </div>
                <ArrowRight className="w-5 h-5 text-navy/30 group-hover:text-gold group-hover:translate-x-1 transition" />
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
